import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowDown, Zap, Globe, Shield, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  fadeInLeft,
  fadeInRight,
} from "@/components/animations/ScrollAnimations";
import { CertificationBadgeGroup } from "@/components/CertificationBadge";

export function HeroSectionNew() {
  return (
    <section className="relative min-h-screen flex flex-col pt-20 overflow-hidden">
      {/* Video Background with dark overlay for text contrast */}
      <div className="absolute inset-0">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline 
          className="absolute w-full h-full object-cover"
        >
          <source src="/videos/hero-video.mp4" type="video/mp4" />
        </video>
        {/* Dark overlay for text readability on light theme */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/70 via-[#0a1628]/50 to-[#0a1628]/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/60 via-transparent to-transparent" />
      </div>

      {/* Subtle ambient orbs - reduced intensity */}
      <motion.div
        className="absolute top-1/4 right-[10%] w-[500px] h-[500px] rounded-full pointer-events-none ambient-orb"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 60%)',
          filter: 'blur(80px)'
        }}
      />
      <motion.div
        className="absolute bottom-1/3 left-[5%] w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--accent) / 0.05) 0%, transparent 60%)',
          filter: 'blur(100px)'
        }}
        animate={{ 
          scale: [1, 1.1, 1], 
          opacity: [0.2, 0.3, 0.2],
          x: [0, 20, 0],
          y: [0, -15, 0]
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex-1 flex flex-col">
        {/* Hero Grid */}
        <div className="flex-1 grid lg:grid-cols-12 gap-8 lg:gap-16 items-center py-12 lg:py-0">
          {/* Left - Main Content */}
          <div className="lg:col-span-7 space-y-10">
            {/* Headline - Larger, bolder */}
            <ScrollReveal delay={0.2} variants={fadeInLeft}>
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold leading-[0.9] tracking-tight">
                <span className="text-white block">Power the</span>
                <span className="text-gradient block py-2">Electric</span>
                <span className="text-white block">Future</span>
              </h1>
            </ScrollReveal>

            {/* Subheadline - Refined */}
            <ScrollReveal delay={0.3}>
              <p className="text-lg md:text-xl text-white/70 max-w-xl leading-relaxed font-light">
                Enterprise-grade EV charging platform for operators, fleets, and utilities. 
                Deploy, manage, and scale with unmatched security and reliability.
              </p>
            </ScrollReveal>

            {/* CTAs - More prominent */}
            <ScrollReveal delay={0.4}>
              <div className="flex flex-wrap gap-4">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <a href="https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/" target="_blank" rel="noopener noreferrer">
                    <Button variant="hero" size="lg" className="group text-base px-8 py-6 shadow-lg shadow-primary/20">
                      Request Demo
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </a>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all text-base px-8 py-6" 
                    asChild
                  >
                    <Link to="/solutions">Explore Solutions</Link>
                  </Button>
                </motion.div>
              </div>
            </ScrollReveal>
          </div>

          {/* Right - Stats Grid - Enhanced */}
          <ScrollReveal variants={fadeInRight} delay={0.3} className="lg:col-span-5">
            {/* Live Status Badge - Above Stats */}
            <motion.a 
              href="https://evg-status.evgateway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2.5 rounded-full hover:border-green-500/50 transition-colors cursor-pointer mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-40 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <span className="text-sm font-medium text-white">
                All Systems Operational
              </span>
            </motion.a>
            
            <StaggerContainer className="grid grid-cols-2 gap-4" staggerDelay={0.1}>
              {[
                { value: "20K+", label: "Charging Ports", icon: Zap, isPrimary: true },
                { value: "99.9%", label: "Platform Uptime", icon: Activity, isPrimary: true },
                { value: "150+", label: "Enterprise Clients", icon: Globe, isPrimary: false },
                { value: "24/7", label: "Global Support", icon: Shield, isPrimary: false },
              ].map((stat, idx) => (
                <StaggerItem key={idx}>
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    className={`bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 transition-all duration-300 group hover:shadow-lg ${stat.isPrimary ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stat.isPrimary ? "bg-primary/20" : "bg-accent/20"}`}>
                        <stat.icon className={`h-4 w-4 ${stat.isPrimary ? "text-primary" : "text-accent"}`} />
                      </div>
                    </div>
                    <div className={`text-3xl md:text-4xl font-bold mb-1 tracking-tight ${stat.isPrimary ? "text-primary" : "text-accent"}`}>
                      {stat.value}
                    </div>
                    <div className="text-sm text-white/70 font-medium">{stat.label}</div>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>

            {/* Compliance Pills - Refined */}
            <motion.div
              className="flex flex-wrap gap-2 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <CertificationBadgeGroup badges={["soc2", "iso27001", "ocpp", "openadr"]} size="sm" />
            </motion.div>
          </ScrollReveal>
        </div>

        {/* Bottom Bar - Sleeker */}
        <motion.div
          className="border-t border-white/10 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Trust Indicators */}
            <div className="flex items-center gap-8 text-sm text-white/60 flex-wrap">
              {[
                { icon: Zap, label: "Hardware Agnostic" },
                { icon: Globe, label: "Global Network" },
                { icon: Shield, label: "Enterprise Security" },
              ].map((item, idx) => (
                <motion.span 
                  key={idx}
                  className="flex items-center gap-2 hover:text-white transition-colors"
                  whileHover={{ x: 2 }}
                >
                  <item.icon className="h-4 w-4 text-primary" />
                  {item.label}
                </motion.span>
              ))}
            </div>

            {/* Scroll Indicator */}
            <motion.a
              href="#solutions"
              className="flex items-center gap-3 text-white/60 hover:text-primary transition-colors group"
              animate={{ y: [0, 4, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-xs uppercase tracking-widest font-medium">Discover More</span>
              <div className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center group-hover:border-primary/50 transition-colors">
                <ArrowDown className="h-4 w-4" />
              </div>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}